/**
 * TOSS Web Application - Axios Interceptors
 *
 * Request and response interceptors for:
 * - Adding JWT access token to requests
 * - Naming the browser's time zone (`X-Client-Timezone`) on every request
 * - Handling 401 responses with automatic token refresh
 * - Queueing requests during token refresh
 * - Redirecting to login on auth failure
 */

import {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import type { RefreshTokenRequest, RefreshTokenResponse } from '@/types';
import { getBrowserTimeZone } from '@/utils/date';

// ============================================================================
// Constants
// ============================================================================

/**
 * The header that tells the API which zone the browser draws dates in. Place-less date columns
 * (created at, received at, audit time) are rendered in the browser's zone, so the server reads their
 * filter days and header-tree days in the same zone (ADR-046: the day in the tree is the date in the
 * cell). Without it the server falls back to Moscow.
 */
export const CLIENT_TIMEZONE_HEADER = 'X-Client-Timezone';

/**
 * Local storage keys for JWT tokens
 */
const TOKEN_KEYS = {
  ACCESS_TOKEN: 'toss_access_token',
  REFRESH_TOKEN: 'toss_refresh_token',
  TOKEN_EXPIRES_AT: 'toss_token_expires_at',
  AUDIENCE: 'toss_token_audience',
} as const;

/**
 * Which API space the current session belongs to. Mirrors the backend's `UserAudience`
 * split (phase0-part1/part2): internal staff use `/api/lk/*`, the external customer
 * portal uses `/api/client/*`. Only two values exist today — `/api/mobile/*` is Flutter,
 * which never runs this web bundle.
 */
export type ApiAudience = 'internal' | 'client';

/**
 * API endpoint for token refresh, scoped to the session's audience. Only meaningful
 * post-login (refresh always follows a prior successful login in this browser, which is
 * the only place `TokenStorage.setAudience()` is called), so reading the currently
 * stored audience here is correct — see `getAudienceApiBase()`.
 */
function getRefreshEndpoint(): string {
  return `${getAudienceApiBase()}/auth/refresh`;
}

/**
 * Endpoints that don't require authentication — a flat union across BOTH audiences, not a lookup
 * by the current one. That matters for login itself: while a login request is in flight no session
 * exists yet, so there is no stored audience to resolve, and the request can only be recognised as
 * public by its literal URL. Without that, a failed client login falls into the refresh-retry
 * branch, refreshes with a null token, throws, and dispatches a global `auth:logout` that signs
 * out the user's other tabs.
 *
 * Kept in sync with every `[AllowAnonymous]` action on `LkAuthController` / `ClientAuthController`.
 * `verify-otp` and `reset-password` were absent here for the internal audience too — harmless
 * today (`verify-otp` answers 200 with `success: false`, `reset-password` throws 400, and
 * `responseErrorHandler` returns early for anything that is not a 401), but this list means
 * "needs no auth", and letting it drift from the backend's anonymous set is exactly how the
 * cascade above reappears the day one of them starts answering 401.
 *
 * `register` and `verify-mentor` are internal-only; the portal has no self-registration.
 */
const PUBLIC_ENDPOINTS = [
  '/api/lk/auth/login',
  '/api/lk/auth/register',
  '/api/lk/auth/forgot-password',
  '/api/lk/auth/verify-otp',
  '/api/lk/auth/reset-password',
  '/api/lk/auth/verify-mentor',
  '/api/lk/auth/refresh',
  '/api/client/auth/login',
  '/api/client/auth/forgot-password',
  '/api/client/auth/verify-otp',
  '/api/client/auth/reset-password',
  '/api/client/auth/refresh',
];

/**
 * Shared predicate for the request/response interceptors below (was duplicated inline
 * in both places before the client audience made a second prefix necessary).
 */
function isPublicEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  return PUBLIC_ENDPOINTS.some((endpoint) => url.startsWith(endpoint));
}

// ============================================================================
// Token Storage
// ============================================================================

/**
 * Token storage utilities for managing JWT tokens in localStorage
 */
export const TokenStorage = {
  /**
   * Get the access token from storage
   */
  getAccessToken(): string | null {
    return localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
  },

  /**
   * Get the refresh token from storage
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
  },

  /**
   * Get the token expiration timestamp
   */
  getExpiresAt(): Date | null {
    const expiresAt = localStorage.getItem(TOKEN_KEYS.TOKEN_EXPIRES_AT);
    return expiresAt ? new Date(expiresAt) : null;
  },

  /**
   * Store tokens after login or refresh
   */
  setTokens(accessToken: string, refreshToken: string, expiresAt: string): void {
    localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
    localStorage.setItem(TOKEN_KEYS.TOKEN_EXPIRES_AT, expiresAt);
  },

  /**
   * Get the audience the stored session belongs to. Defaults to `'internal'` when
   * unset — both for a browser that never logged in, and (critically) for a browser
   * that already holds tokens from before this field existed. That default is what
   * keeps every existing internal session working unchanged after this ships.
   */
  getAudience(): ApiAudience {
    return localStorage.getItem(TOKEN_KEYS.AUDIENCE) === 'client' ? 'client' : 'internal';
  },

  /**
   * Record which audience the just-issued tokens belong to. Called once, from
   * `authStore.setAuth()`, derived from the logged-in user's role — never touched by
   * the refresh flow, which always keeps the audience of the session it's refreshing.
   */
  setAudience(audience: ApiAudience): void {
    localStorage.setItem(TOKEN_KEYS.AUDIENCE, audience);
  },

  /**
   * Clear all tokens (on logout or auth failure)
   */
  clearTokens(): void {
    localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.TOKEN_EXPIRES_AT);
    localStorage.removeItem(TOKEN_KEYS.AUDIENCE);
  },

  /**
   * Check if user has valid tokens stored
   */
  hasTokens(): boolean {
    return !!this.getAccessToken() && !!this.getRefreshToken();
  },

  /**
   * Check if access token is expired or about to expire (within 30 seconds)
   */
  isTokenExpired(): boolean {
    const expiresAt = this.getExpiresAt();
    if (!expiresAt) {
      return true;
    }
    // Consider expired if less than 30 seconds remaining
    const bufferMs = 30 * 1000;
    return new Date().getTime() > expiresAt.getTime() - bufferMs;
  },
};

/**
 * API base path for the current session's audience — `/api/lk` (internal, unchanged
 * default) or `/api/client` (external customer portal). The `/api/client/*` space
 * mirrors `/api/lk/*`'s route shape one-for-one (phase0-part2-client-role-and-schema.md
 * §1), so every audience-dependent endpoint elsewhere is built as
 * `` `${getAudienceApiBase()}/<same-suffix-as-lk>` ``.
 */
export function getAudienceApiBase(): string {
  return TokenStorage.getAudience() === 'client' ? '/api/client' : '/api/lk';
}

// ============================================================================
// Token Refresh Queue
// ============================================================================

/**
 * A request parked while a refresh is in flight. BOTH halves are kept: a queue that can only
 * resolve has no way to say "the session is gone", and dropping the subscriber instead leaves the
 * request pending forever — which TanStack Query renders as a spinner that never stops and never
 * errors. That was the real shape of the bug: the queue's leader was rejected and logged out
 * correctly, while the three requests behind it simply vanished.
 */
interface RefreshSubscriber {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}

/**
 * State for managing token refresh and request queueing
 */
let isRefreshing = false;
let refreshSubscribers: RefreshSubscriber[] = [];

/**
 * Subscribe to token refresh completion
 */
function subscribeToRefresh(subscriber: RefreshSubscriber): void {
  refreshSubscribers.push(subscriber);
}

/**
 * Notify all subscribers when token is refreshed.
 *
 * The list is emptied BEFORE the callbacks run: a callback retries its request, and a retry that
 * 401s again would subscribe anew — assigning `[]` afterwards would then erase a subscriber that
 * had just joined for the NEXT refresh.
 */
function onRefreshSuccess(token: string): void {
  const waiting = refreshSubscribers;
  refreshSubscribers = [];
  waiting.forEach(({ resolve }) => resolve(token));
}

/**
 * Handle refresh failure - reject the whole queue, then clear the session.
 *
 * Everyone parked behind the refresh gets the SAME verdict the leader got (the refresh error, not
 * their own 401), because they failed for the same reason: there is no session left to retry with.
 */
function onRefreshFailure(error: unknown): void {
  const waiting = refreshSubscribers;
  refreshSubscribers = [];
  waiting.forEach(({ reject }) => reject(error));
  // Clear tokens and redirect to login
  TokenStorage.clearTokens();
  // Dispatch custom event for auth state change
  window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason: 'token_refresh_failed' } }));
}

// ============================================================================
// Request Interceptor
// ============================================================================

/**
 * Request interceptor function
 * Adds the browser's zone to every request and the JWT access token to the Authorization header
 */
function requestInterceptor(
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig {
  // Every request, public ones included: it is not a credential, and a list read before the token
  // is attached must still count days in the zone its cells are drawn in.
  const timeZone = getBrowserTimeZone();
  if (timeZone) {
    config.headers[CLIENT_TIMEZONE_HEADER] = timeZone;
  }

  // Skip auth header for public endpoints
  if (isPublicEndpoint(config.url)) {
    return config;
  }

  // Add access token to request
  const accessToken = TokenStorage.getAccessToken();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
}

/**
 * Request error handler
 */
function requestErrorHandler(error: AxiosError): Promise<never> {
  return Promise.reject(error);
}

// ============================================================================
// Response Interceptor
// ============================================================================

/**
 * Response success handler
 */
function responseInterceptor(response: AxiosResponse): AxiosResponse {
  return response;
}

/**
 * Response error handler
 * Handles 401 errors with automatic token refresh
 */
async function responseErrorHandler(
  error: AxiosError,
  axiosInstance: AxiosInstance
): Promise<AxiosResponse> {
  const originalRequest = error.config as InternalAxiosRequestConfig & {
    _retry?: boolean;
    /**
     * Opt OUT of the refresh-and-retry branch below for THIS request (default: unset, i.e.
     * the previous behaviour, byte for byte). Set by a caller whose 401 means "the data you
     * sent is wrong", not "your session is over" — see the check further down.
     */
    skipAuthRefresh?: boolean;
  };

  // Skip if not a 401 error
  if (error.response?.status !== 401) {
    return Promise.reject(error);
  }

  // Skip if this is a retry request or the refresh endpoint itself failing (refresh
  // token dead — session unrecoverable, force a full logout). Checked before the
  // generic isPublicEndpoint() below on purpose: the refresh endpoint is also public
  // endpoint, but its 401 means something more specific than "reject and move on".
  if (originalRequest._retry || originalRequest.url === getRefreshEndpoint()) {
    TokenStorage.clearTokens();
    window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason: 'unauthorized' } }));
    return Promise.reject(error);
  }

  // Skip if this is a public endpoint — covers a failed login itself (e.g. a wrong
  // password) before it can fall through to the refresh-retry logic below and throw
  // "No refresh token available" instead of surfacing the real error.
  if (isPublicEndpoint(originalRequest.url)) {
    return Promise.reject(error);
  }

  /**
   * Same idea, for an AUTHENTICATED endpoint whose 401 is about the request rather than the
   * session. `change-password` is the case that exposed it: a wrong CURRENT password answers
   * 401, the request fell into the branch below, the token refreshed fine, the retry answered
   * 401 again — and the second pass, now carrying `_retry`, cleared the tokens and dispatched
   * `auth:logout`. A typo in one field signed the customer out of every tab.
   *
   * Opt-in per request, so nothing that does not set the flag changes in any way.
   */
  if (originalRequest.skipAuthRefresh) {
    return Promise.reject(error);
  }

  // Mark this request as a retry
  originalRequest._retry = true;

  // If already refreshing, queue this request
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      subscribeToRefresh({
        resolve: (token: string) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(axiosInstance(originalRequest));
        },
        reject,
      });
    });
  }

  // Start refresh process
  isRefreshing = true;

  try {
    const refreshToken = TokenStorage.getRefreshToken();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    // Make refresh request
    const response = await axiosInstance.post<RefreshTokenResponse>(getRefreshEndpoint(), {
      refreshToken,
    } as RefreshTokenRequest);

    const { accessToken, refreshToken: newRefreshToken, expiresAt } = response.data;

    // Store new tokens
    TokenStorage.setTokens(accessToken, newRefreshToken, expiresAt);

    // Notify subscribers
    onRefreshSuccess(accessToken);

    // Retry original request with new token
    originalRequest.headers.Authorization = `Bearer ${accessToken}`;
    return axiosInstance(originalRequest);
  } catch (refreshError) {
    onRefreshFailure(refreshError);
    return Promise.reject(refreshError);
  } finally {
    isRefreshing = false;
  }
}

// ============================================================================
// Setup Functions
// ============================================================================

/**
 * Setup request interceptor on axios instance
 */
export function setupRequestInterceptor(axiosInstance: AxiosInstance): number {
  return axiosInstance.interceptors.request.use(
    requestInterceptor,
    requestErrorHandler
  );
}

/**
 * Setup response interceptor on axios instance
 */
export function setupResponseInterceptor(axiosInstance: AxiosInstance): number {
  return axiosInstance.interceptors.response.use(
    responseInterceptor,
    (error: AxiosError) => responseErrorHandler(error, axiosInstance)
  );
}

// ============================================================================
// Auth Event Listeners Helper
// ============================================================================

/**
 * Add listener for auth logout events
 * Call this from your auth store or app component to handle logout
 *
 * @example
 * ```ts
 * onAuthLogout((reason) => {
 *   // Clear user state
 *   // Redirect to login
 * });
 * ```
 */
export function onAuthLogout(
  callback: (reason: string) => void
): () => void {
  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<{ reason: string }>;
    callback(customEvent.detail.reason);
  };

  window.addEventListener('auth:logout', handler);

  // Return cleanup function
  return () => {
    window.removeEventListener('auth:logout', handler);
  };
}
